import { useCallback, useState } from 'react';
import { TeamTabValue, TEAM_TABS, TEAM_MENU_TABS } from '../types';

const DEFAULT_TAB: TeamTabValue = TEAM_TABS.MEMBER;

/**
 * 저장 가능한 탭인지 확인하는 함수
 * @param value 탭 값
 * @returns 저장 가능한 탭인지 여부
 */
const isSavableTab = (
    value: string
): value is (typeof TEAM_MENU_TABS)[number] => {
    return (TEAM_MENU_TABS as readonly string[]).includes(value);
};

/**
 * 팀별 마지막 방문 탭 관리를 위한 커스텀 훅
 * @param userId 사용자 ID
 * @param teamId 팀 ID
 */
export const useTeamLastTab = (userId: string | undefined, teamId: string) => {
    const getTeamLastTabKey = useCallback(
        (uid: string, tid: string) => `team_${tid}_last_tab_${uid}`,
        []
    );

    // localStorage에서 상태를 가져오거나 기본값 설정
    const [lastTab, setLastTab] = useState<TeamTabValue>(() => {
        if (!userId) return DEFAULT_TAB;

        const savedTab = localStorage.getItem(
            getTeamLastTabKey(userId, teamId)
        );

        if (!savedTab) return DEFAULT_TAB;

        try {
            // 저장 가능한 탭인지 확인 (team과 team_setting 제외)
            if (isSavableTab(savedTab)) {
                return savedTab;
            }
            return DEFAULT_TAB;
        } catch (e) {
            console.error('팀 마지막 탭 파싱 실패:', e);
            return DEFAULT_TAB;
        }
    });

    // 탭 저장 함수
    const saveLastTab = useCallback(
        (tab: TeamTabValue) => {
            if (!userId || !tab) return;

            // 저장 가능한 탭만 저장 (team과 team_setting 제외)
            if (!isSavableTab(tab)) return;

            try {
                localStorage.setItem(getTeamLastTabKey(userId, teamId), tab);
                setLastTab(tab);
            } catch (e) {
                console.error('팀 마지막 탭 저장 실패:', e);
            }
        },
        [userId, teamId, getTeamLastTabKey]
    );

    // 탭 가져오기 함수
    const getLastTab = useCallback(() => {
        if (!userId) return DEFAULT_TAB;

        const savedTab = localStorage.getItem(
            getTeamLastTabKey(userId, teamId)
        );

        if (!savedTab) return DEFAULT_TAB;

        try {
            // 저장 가능한 탭인지 확인 (team과 team_setting 제외)
            if (isSavableTab(savedTab)) {
                return savedTab;
            }
            return DEFAULT_TAB;
        } catch (e) {
            return DEFAULT_TAB;
        }
    }, [userId, teamId, getTeamLastTabKey]);

    return {
        lastTab,
        saveLastTab,
        getLastTab,
    };
};
